"use client";

import type { ParsedUrlQuery } from "querystring";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import React, { useEffect, useState, useRef } from "react";
import ImageCollage from "@/src/shared/components/ImageCollage";
import HeroGallery from "@/src/shared/components/listing/HeroGallery";
import HeroCollapsedBar from "@/src/shared/components/listing/HeroCollapsedBar";
import Layout from "@/src/components/layout";
import SEO from "@/src/shared/components/SEO";
import type { IListing } from "@/src/shared/types/listing";
import ShareButton from "@/src/shared/components/ShareButton";
import { WishlistHeartButton } from "@/src/shared/features/wishlist";
import AboutDetailsModal from "@/src/shared/components/AboutDetailsModal";
import DatePicker from "@/src/shared/components/DatePicker";
import { type IFilter } from "@/src/shared/features/search";
import { formatLocalDate } from "@/src/shared/utils/formateDate";
import {
  MeetYourHost,
  BookNowCard,
  CheckInOutTimeline,
  ContactHostDialog,
  FixedBottomReserveBar,
  HostedByRow,
  ListingAmenities,
  ListingHighlights,
  ListingLocation,
  ListingTitleBlock,
  ReportListing,
  ThingsToKnow,
  useBlockedDates,
  useBoostClickTracker,
} from "@/src/features/listing";
import { ListingReviews } from "@/src/shared/features/reviews";
import { useWindowSize } from "@/src/shared/hooks/useWindowSize";
import { formatBaths } from "@/src/shared/utils/global";
import { useAuth } from "@/src/core/auth/useAuth";
import { APP_URL } from "@/src/core/config/app";
import FooterMain from "@/src/components/layout/footer";
import { ListingFaqSection } from "@/src/features/faq";

const ListingView = ({ listing }: { listing: IListing }) => {
  const router = useAppRouter();
  const { isLg, width } = useWindowSize();
  const { requireAuth, user_profile } = useAuth();
  const [isPendingEvent, setPendingEvent] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { blockedDates, checkoutOnlyDates, loading, error } = useBlockedDates(
    listing.id,
  );
  // Boost analytics — one `click` event per details page view.
  useBoostClickTracker(listing?.id);

  useEffect(() => {
    setIsClient(true);
    setIsMobile(width <= 768);
  }, [width]);

  const isUpdatingUrl = useRef(false);

  const { location, placeName } = router.query;
  const [open, setOpen] = useState(false);

  const [filters, setFilters] = useState<IFilter>({
    destination: null,
    checkIn: null,
    checkOut: null,
    guests: { adults: 1, children: 0, infants: 0 },
  });

  // Parse query (either a URL string or a query object) and apply to filters
  const applyQueryToFilters = (source: string | ParsedUrlQuery) => {
    const newFilters: IFilter = {
      destination: null,
      checkIn: null,
      checkOut: null,
      guests: { adults: 1, children: 0, infants: 0 },
    };

    if (typeof source === "string") {
      // source is a path or search string like '/listing/123?from=..' or '?from=..'
      try {
        const parsed = source.startsWith("?")
          ? new URL(window.location.href)
          : new URL(source, window.location.origin);
        const params = parsed.searchParams;
        const from = params.get("from");
        const to = params.get("to");
        const guest = params.get("guest");
        const child = params.get("child");
        const infant = params.get("infant");
        const addressName = params.get("address_name");

        if (addressName)
          newFilters.destination = {
            name: addressName,
            lat: null,
            lng: null,
            id: 0,
            name_bn: null,
            order: 0,
            tier_1: null,
            tier_2: null,
            within: null,
          };
        if (from) newFilters.checkIn = new Date(from as string);
        if (to) newFilters.checkOut = new Date(to as string);
        if (guest) newFilters.guests.adults = parseInt(guest, 10) || 1;
        if (child) newFilters.guests.children = parseInt(child, 10) || 0;
        if (infant) newFilters.guests.infants = parseInt(infant, 10) || 0;
      } catch (_e) {
        // fallback to current location search - only run client-side
        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          const from = params.get("from");
          const to = params.get("to");
          if (from) newFilters.checkIn = new Date(from as string);
          if (to) newFilters.checkOut = new Date(to as string);
          const guest = params.get("guest");
          if (guest) newFilters.guests.adults = parseInt(guest, 10) || 1;
        }
      }
    } else if (typeof source === "object" && source !== null) {
      const query = source;
      if (
        query.slug &&
        typeof query.slug === "string" &&
        query.slug.includes("nearby")
      ) {
        newFilters.destination = {
          name: "Nearby",
          lat: null,
          lng: null,
          id: 0,
          name_bn: null,
          order: 0,
          tier_1: null,
          tier_2: null,
          within: null,
        };
      } else if (query.address_name && typeof query.address_name === "string") {
        newFilters.destination = {
          name: query.address_name as string,
          lat: null,
          lng: null,
          id: 0,
          name_bn: null,
          order: 0,
          tier_1: null,
          tier_2: null,
          within: null,
        };
      }

      if (query.from && typeof query.from === "string")
        newFilters.checkIn = new Date(query.from);
      if (query.to && typeof query.to === "string")
        newFilters.checkOut = new Date(query.to);
      if (
        query.guest &&
        (typeof query.guest === "string" || typeof query.guest === "number")
      )
        newFilters.guests.adults = parseInt(String(query.guest), 10) || 1;
      if (
        query.child &&
        (typeof query.child === "string" || typeof query.child === "number")
      )
        newFilters.guests.children = parseInt(String(query.child), 10) || 0;
      if (
        query.infant &&
        (typeof query.infant === "string" || typeof query.infant === "number")
      )
        newFilters.guests.infants = parseInt(String(query.infant), 10) || 0;
    }

    // Use functional setState to avoid closure over stale `filters` and ensure we only update when different
    setFilters((prev) => {
      if (!isFiltersEqual(prev, newFilters)) return newFilters;
      return prev;
    });
  };

  // Browser back/forward within this page: apply the URL it lands on. A move to
  // another listing remounts the page, which reads its URL afresh.
  useEffect(() => {
    const onPopState = () => {
      // Skip if we're updating URL programmatically
      if (isUpdatingUrl.current) return;

      // browser back/forward -> apply actual location
      applyQueryToFilters(window.location.href);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Track previous listing id to detect route changes between listings
  const prevListingIdRef = React.useRef<string | string[] | undefined>(
    undefined,
  );

  // When listing id changes (navigating to a different listing), force-apply query params
  useEffect(() => {
    // Skip if we're updating URL programmatically
    if (isUpdatingUrl.current) return;

    const currentId = router.query.id;
    const prevId = prevListingIdRef.current;
    if (prevId !== undefined && currentId !== prevId) {
      // Force parse current router.query and apply to filters for the new listing
      applyQueryToFilters(router.query);
    }
    prevListingIdRef.current = currentId;
  }, [router.query.id]);

  // Also apply filters on initial mount / when router.asPath changes to ensure state matches URL
  useEffect(() => {
    // Skip if we're updating URL programmatically
    if (isUpdatingUrl.current) return;

    if (!router.isReady) return;
    // apply current path (includes query string)
    applyQueryToFilters(router.asPath || window.location.href);
  }, [router.isReady, router.asPath, listing?.id]);

  useEffect(() => {
    if (user_profile && isPendingEvent) {
      // User just logged in and we have pending booking data
      setOpen(true);
      setPendingEvent(false);
    }
  }, [user_profile, isPendingEvent]);

  const handleHostModalOpen = () => {
    requireAuth(
      () => {
        setOpen(true);
      },
      () => {
        // This callback runs when login modal opens (user not authenticated)
        // Store the booking data to submit after login
        setPendingEvent(true);
      },
    );
  };

  const handleHostModalClose = () => {
    setOpen(false);
  };

  // "Book" on the contact screen: the reserve sheet on a phone, the booking card on desktop.
  const [reserveRequest, setReserveRequest] = useState(0);
  const handleBookFromContact = () => {
    if (window.innerWidth < 768) setReserveRequest((n) => n + 1);
    else
      document
        .getElementById("book-now-card")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Truly silent URL update function - uses History API directly
  const updateUrlSilently = (updates: Record<string, string | number>) => {
    if (typeof window === "undefined") return;

    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;

    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      } else {
        params.delete(key);
      }
    });

    const listingPathname = listing?.id
      ? `/listing/${listing.id}`
      : router.query.id
        ? `/listing/${router.query.id}`
        : currentUrl.pathname;
    const newUrl = `${listingPathname}${params.toString() ? "?" + params.toString() : ""}`;

    // Only update if URL actually changed
    if (newUrl !== window.location.href) {
      // Set flag to prevent event handlers from running
      isUpdatingUrl.current = true;

      // Use replaceState for silent updates without affecting history
      // No state of ours: Next copies its own in and its router follows the URL, with
      // no server request.
      window.history.replaceState(null, "", newUrl);

      // Reset flag after a brief delay
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 100);
    }
  };

  // Hoisted equality helper so it can be used before definition
  function isFiltersEqual(a: IFilter, b: IFilter) {
    const aFrom = a.checkIn ? formatLocalDate(a.checkIn) : "";
    const bFrom = b.checkIn ? formatLocalDate(b.checkIn) : "";
    const aTo = a.checkOut ? formatLocalDate(a.checkOut) : "";
    const bTo = b.checkOut ? formatLocalDate(b.checkOut) : "";

    return (
      aFrom === bFrom &&
      aTo === bTo &&
      (a.guests?.adults || 1) === (b.guests?.adults || 1) &&
      (a.guests?.children || 0) === (b.guests?.children || 0) &&
      (a.guests?.infants || 0) === (b.guests?.infants || 0) &&
      (a.message || "") === (b.message || "")
    );
  }

  const handleUpdateQuery = () => {
    const { checkIn, checkOut, guests, message } = filters;

    const updates: Record<string, string | number> = {};
    if (guests?.adults) updates.guest = guests.adults;
    if (guests?.children) updates.child = guests.children;
    if (guests?.infants) updates.infant = guests.infants;
    if (checkIn) updates.from = formatLocalDate(checkIn);
    if (checkOut) updates.to = formatLocalDate(checkOut);
    if (message) updates.message = message;

    // Update URL silently without any re-renders
    updateUrlSilently(updates);
  };

  // Generate SEO-optimized title and description for social sharing
  const getTitle = (listing: IListing) => {
    return `${listing.title} - ৳${listing.price || listing.showable_price} | Travela`;
  };

  const getDescription = (listing: IListing) => {
    const plural = (n: number, s: string, p?: string) =>
      `${n} ${n === 1 ? s : p || s + "s"}`;
    const guestsText = plural(listing?.max_guest ?? 0, "guest");
    const bedroomsText =
      (listing?.bedroom ?? 0) > 0
        ? plural(listing.bedroom, "bedroom")
        : "Studio";
    const bedsText = plural(listing?.beds ?? 0, "bed");
    const bathsText = formatBaths(listing?.bathroom);
    const price =
      listing.min_price ||
      listing.price ||
      listing.showable_price ||
      listing.average_price;

    if (listing.hotel_profile) {
      return `Book your stay at ${listing.hotel_profile.title} in ${listing.hotel_profile.address}. ${listing.hotel_profile.meta.hotel_star_rating} star hotel with premium facilities. Starting from ৳${price} per night.`;
    }

    return `${guestsText} · ${bedroomsText} · ${bedsText} · ${bathsText} | Starting from ৳${price} per day. ${listing.title}`;
  };

  // Build a shareable URL from current filters and listing id
  const buildListingUrl = (f: IFilter) => {
    if (!listing)
      return typeof window !== "undefined" ? window.location.href : "";
    const base = `${APP_URL}/listing/${listing.id}`;
    const params = new URLSearchParams();

    if (f.checkIn) params.set("from", formatLocalDate(f.checkIn));
    if (f.checkOut) params.set("to", formatLocalDate(f.checkOut));
    if (f.guests?.adults) params.set("guest", String(f.guests.adults));
    if (f.guests?.children) params.set("child", String(f.guests.children));
    if (f.guests?.infants) params.set("infant", String(f.guests.infants));

    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const shareUrl = buildListingUrl(filters);

  const scrollToReviews = () =>
    document
      .getElementById("reviews")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  const highlights = listing.highlights ?? [];
  const facilities = listing.facilities ?? [];
  const isOwnListing = !!user_profile && user_profile.id === listing.host?.id;

  return (
    <>
      <SEO
        title={getTitle(listing)}
        description={getDescription(listing)}
        images={listing.images?.slice(0, 3) || []}
        url={shareUrl}
        canonical={`${APP_URL}/listing/${listing.id}`}
        ogType="product"
        keywords={[
          listing.title,
          listing.place_type,
          "Bangladesh",
          "hotel booking",
          "vacation rental",
          "short stay",
        ].filter(Boolean)}
      />
      <Layout isSearchHide={isClient ? isMobile : false}>
        <div className="bg-white md:pt-8">
          <div className="mx-auto max-w-7xl md:px-6 lg:px-8">
            {/* Desktop: the title over the photos, with save and share. */}
            <div className="hidden items-start justify-between gap-6 md:flex">
              <h1 className="text-24 font-semibold leading-8 text-ink">
                {listing.title}
              </h1>
              {/* Airbnb's pair: an underlined "Share", then "Save". */}
              <div className="flex shrink-0 items-center">
                <ShareButton url={shareUrl} variant="text" />
                <WishlistHeartButton listingId={listing.id} variant="labeled" />
              </div>
            </div>

            {/* Mobile: the app's collapsed bar once the photo scrolls away. */}
            <HeroCollapsedBar listingId={listing.id} title={listing.title} />

            <div className="relative md:mt-6">
              <ImageCollage
                singleListingData={listing}
                images={listing?.images || []}
                // Mobile: the app's hero (bubbles, "Show more", pinned photo).
                mobileHero={(openGallery) => (
                  <HeroGallery
                    listingId={listing.id}
                    images={listing.images ?? []}
                    shareUrl={shareUrl}
                    onOpenGallery={openGallery}
                  />
                )}
              />
            </div>

            {/* Mobile: the app's white sheet rising over the photo. */}
            <div className="relative z-10 -mt-6 rounded-t-3xl bg-white px-5 pb-6 pt-6 md:hidden">
              <ListingTitleBlock
                listing={listing}
                centered
                asHeading
                animateFavourite
                onShowReviews={scrollToReviews}
              />
            </div>

            {/* Opaque and above the hero: the pinned photo must not show through. */}
            <div className="relative grid grid-cols-12 gap-8 bg-white px-4 md:mt-8 md:px-0 lg:gap-16">
              {/* Sections in the app's order; the web's extras sit where they fit. */}
              <div className="col-span-12 md:col-span-7">
                <div className="hidden pb-8 md:block">
                  <ListingTitleBlock
                    listing={listing}
                    showTitle={false}
                    onShowReviews={scrollToReviews}
                  />
                </div>

                {(listing.check_in || listing.check_out) && (
                  <ListingSection>
                    <CheckInOutTimeline
                      checkIn={listing.check_in}
                      checkOut={listing.check_out}
                    />
                  </ListingSection>
                )}

                {listing.host &&
                  (listing.host.first_name || listing.host.last_name) && (
                    <ListingSection>
                      <HostedByRow host={listing.host} />
                    </ListingSection>
                  )}

                {highlights.length > 0 && (
                  <ListingSection>
                    <ListingHighlights highlights={highlights} />
                  </ListingSection>
                )}

                {listing.description && (
                  <ListingSection>
                    <h2 className="text-xl font-semibold text-ink md:text-22">
                      About this place
                    </h2>
                    <AboutDetailsModal
                      content={listing.description}
                      maxLines={8}
                      showTitle={false}
                      title="About this space"
                      buttonText="Show more"
                      className="mt-4"
                      contentClassName="!my-0 text-sm leading-6 text-ink md:text-base"
                      buttonClassName="!mt-6 !flex !h-[46px] !w-full !items-center !justify-center !rounded-xl !border-0 !bg-[#EBEBEB] !px-6 !text-base !font-semibold !text-ink md:!w-auto"
                    />
                  </ListingSection>
                )}

                {/* The web's calendar, right after the description (the app has an Availability row). */}
                <ListingSection>
                  <h2 className="text-xl font-semibold text-ink md:text-22">
                    Availability
                  </h2>
                  <div className="mt-2">
                    <DatePicker
                      isDetailsPage={true}
                      selectedDates={{
                        checkIn: filters.checkIn,
                        checkOut: filters.checkOut,
                      }}
                      onDateChange={(dates) =>
                        setFilters((f) => ({ ...f, ...dates }))
                      }
                      onConfirm={handleUpdateQuery}
                      containerClass={`!border-none !shadow-none`}
                      isMonthSingleView={isLg}
                      blockedDates={blockedDates}
                      checkoutOnlyDates={checkoutOnlyDates}
                      loading={loading}
                      error={error}
                    />
                  </div>
                </ListingSection>

                {facilities.length > 0 && (
                  <ListingSection>
                    <ListingAmenities facilities={facilities} />
                  </ListingSection>
                )}

                <ListingSection>
                  <ListingReviews
                    listingId={listing.id}
                    reviewsAvg={listing.reviews_avg}
                    reviewsCount={listing.reviews_count}
                    badges={listing.badges}
                  />
                </ListingSection>

                {(listing.location || listing.google_map_cache) && (
                  <ListingSection>
                    <ListingLocation listing={listing} />
                  </ListingSection>
                )}

                {listing.host && (
                  <ListingSection>
                    <MeetYourHost
                      handleHostModalOpen={handleHostModalOpen}
                      host={listing.host}
                      coHosts={listing.co_hosts}
                      reviewsAvg={listing.reviews_avg}
                      reviewsCount={listing.reviews_count}
                    />
                  </ListingSection>
                )}

                <ListingSection>
                  <ThingsToKnow listing={listing} />
                </ListingSection>

                {!isOwnListing && (
                  <ListingSection>
                    <ReportListing listingId={listing.id} />
                  </ListingSection>
                )}
              </div>

              {/* Desktop booking card, kept in view while the sections scroll. */}
              <aside className="col-span-5 hidden md:block">
                <div id="book-now-card" className="sticky top-28">
                  <BookNowCard
                    filters={filters}
                    setFilters={setFilters}
                    handleUpdateQuery={handleUpdateQuery}
                    singleListingData={listing}
                    location={location && location}
                    placeName={placeName && placeName}
                    blockedDates={blockedDates}
                    checkoutOnlyDates={checkoutOnlyDates}
                  />
                </div>
              </aside>
            </div>

            {/* Last section before the footer — property FAQ */}
            <div className="mt-6 border-t border-[#EBEBEB] px-4 pb-16 pt-12 md:px-0 md:pb-20 md:pt-16">
              <ListingFaqSection />
            </div>
          </div>
          <FooterMain />
        </div>

        <ContactHostDialog
          open={open}
          onClose={handleHostModalClose}
          onBook={handleBookFromContact}
          singleListingData={listing}
          filters={filters}
          setFilters={setFilters}
          handleUpdateQuery={handleUpdateQuery}
          blockedDates={blockedDates}
          checkoutOnlyDates={checkoutOnlyDates}
        />
        <div className="flex pb-24 md:hidden" />
        <FixedBottomReserveBar
          blockedDates={blockedDates}
          checkoutOnlyDates={checkoutOnlyDates}
          handleUpdateQuery={handleUpdateQuery}
          filters={filters}
          setFilters={setFilters}
          listing={listing}
          hide={open}
          openRequest={reserveRequest}
        />
      </Layout>
    </>
  );
};

/** One section of the page: a hairline above, the app's 24px gaps (more on desktop). */
const ListingSection: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <section className="border-t border-[#EBEBEB] py-6 md:py-10">
    {children}
  </section>
);

export default ListingView;
